import { c, msgid, ngettext } from 'ttag';

import { MembershipDisplayStatus } from '../../graphql/__generated__/schema';
import { PortalUserQuery } from '../../graphql/__generated__/types';

type PortalUser = NonNullable<PortalUserQuery['portalUser']>;

export type InvitationState =
    | { kind: 'none' }
    /** Invited with no link at all: the state reopening an access produces. */
    | { kind: 'missing'; sentCount: number }
    | { kind: 'pending'; days: number; sentCount: number }
    | { kind: 'expiring'; days: number; sentCount: number }
    | { kind: 'expired'; days: number; sentCount: number };

const EXPIRING_SOON_DAYS = 3;

const wholeDaysBetween = (from: Date, to: Date): number =>
    Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));

const hasPendingAccess = (user: PortalUser): boolean =>
    (user.memberships ?? []).some(
        (membership) =>
            membership?.status === MembershipDisplayStatus.Invited ||
            membership?.status === MembershipDisplayStatus.InvitationExpired
    );

export const invitationStateOf = (user: PortalUser, now: Date = new Date()): InvitationState => {
    if (user.activatedAt) {
        return { kind: 'none' };
    }
    if (!user.invitationExpiresAt) {
        return hasPendingAccess(user)
            ? { kind: 'missing', sentCount: user.invitationSentCount ?? 0 }
            : { kind: 'none' };
    }

    const sentCount = user.invitationSentCount ?? 1;
    const expiry = new Date(user.invitationExpiresAt);
    const days = wholeDaysBetween(now, expiry);

    if (days < 0) {
        return { kind: 'expired', days: Math.abs(days), sentCount };
    }
    return { kind: days <= EXPIRING_SOON_DAYS ? 'expiring' : 'pending', days, sentCount };
};

export const invitationSentence = (state: InvitationState): string | null => {
    switch (state.kind) {
        case 'missing':
            return c('Invitation state')
                .t`This person is invited but holds no activation link: reopening an access does not create one, and the closure had cancelled the one that was pending. Nothing will reach them until the invitation is sent again.`;
        case 'expired':
            return ngettext(
                msgid`This invitation expired ${state.days} day ago: the link it carried no longer works.`,
                `This invitation expired ${state.days} days ago: the link it carried no longer works.`,
                state.days
            );
        case 'expiring':
            // Zero means today.
            if (state.days === 0) {
                return c('Invitation state').t`This invitation expires today.`;
            }
            return ngettext(
                msgid`This invitation expires in ${state.days} day.`,
                `This invitation expires in ${state.days} days.`,
                state.days
            );
        case 'pending':
            return ngettext(
                msgid`This invitation is valid for another ${state.days} day.`,
                `This invitation is valid for another ${state.days} days.`,
                state.days
            );
        default:
            return null;
    }
};

export const invitationChaseSentence = (state: InvitationState): string | null => {
    if (state.kind === 'none' || state.sentCount <= 1) {
        return null;
    }
    const count = state.sentCount;
    return ngettext(
        msgid`It has already been sent ${count} time. If it keeps going unanswered, check the address before sending again.`,
        `It has already been sent ${count} times. If it keeps going unanswered, check the address before sending again.`,
        count
    );
};

/** Only data-readable refusals are pre-empted here; permission refusals come after the click. */
export const resendBlocker = (user: PortalUser): string | null => {
    if (user.activatedAt) {
        return c('Invitation blocker')
            .t`This person has already activated their account: there is no invitation left to send. They sign in with their password.`;
    }
    if (user.disabledAt) {
        return c('Invitation blocker')
            .t`This person's account is disabled. Reactivating it comes first: an invitation to a disabled person leads nowhere, whatever their accesses say.`;
    }
    if ((user.memberships ?? []).length > 0 && !hasPendingAccess(user)) {
        return c('Invitation blocker')
            .t`This person holds no pending access. Reopen a membership first: there is nothing to invite them to.`;
    }
    return null;
};
